import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { usePageNavigate } from "./hooks/usePageNavigate";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { LogIn, LogOut, User, LayoutDashboard, Bell, Loader2, ChevronDown, Search, Store, Briefcase, Bookmark, Shield, Handshake, Banknote, Menu, X, MessageCircle, FileText, TrendingUp, Users, DollarSign, AlertTriangle, ArrowRight, Clock, Building2, CheckCircle } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { GoldButton, Logo } from "./components/shared/UIComponents";
import AuthPage from "./pages/AuthPage";
import BorrowerDashboard from "./pages/BorrowerDashboard";
import LenderPortal from "./pages/LenderPortal";
import AdminDashboard from "./pages/AdminDashboard";
import ProfilePage from "./pages/ProfilePage";
import LenderDiscovery from "./pages/LenderDiscovery";
import LoanMarket from "./pages/LoanMarket";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import LoanProgramsPage from "./pages/LoanProgramsPage";
import CapitalPartnersPage from "./pages/CapitalPartnersPage";
import FoundingMembersPage from "./pages/FoundingMembersPage";
import ApplyPage from "./pages/ApplyPage";
import ContactPage from "./pages/ContactPage";
import PendingApproval from "./pages/PendingApproval";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RiskDisclosurePage from "./pages/RiskDisclosurePage";
import AccreditedNoticePage from "./pages/AccreditedNoticePage";
import PlatformRolePage from "./pages/PlatformRolePage";
import LenderOnboardPage from "./pages/LenderOnboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import InvestorDashboard from "./pages/InvestorDashboard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster, toast } from "sonner";
import InvestorOnboardingPage from "./pages/InvestorOnboardingPage";
import InsightsPage from "./pages/InsightsPage";
import ChatWidget from "./components/ChatWidget";
import InternalLoginPage from "./pages/InternalLoginPage";
import InvestorLoginPage from "./pages/InvestorLoginPage";
import LenderLoginPage from "./pages/LenderLoginPage";
import ConditionalAccessView from "./pages/ConditionalAccessView";
import Deal360Page from "./pages/Deal360Page";
import Borrower360Page from "./pages/Borrower360Page";
import Lender360Page from "./pages/Lender360Page";
import Investor360Page from "./pages/Investor360Page";
import Opportunity360Page from "./pages/Opportunity360Page";
import Commitment360Page from "./pages/Commitment360Page";
import Document360Page from "./pages/Document360Page";
import Listing360Page from "./pages/Listing360Page";
import Fund360Page from "./pages/Fund360Page";
import Exception360Page from "./pages/Exception360Page";
import Distribution360Page from "./pages/Distribution360Page";
import ReportCenterPage from "./pages/ReportCenterPage";
import Report360Page from "./pages/Report360Page";
import SecondaryMarketPage from "./pages/SecondaryMarketPage";
import InvestorMarketplacePage from "./pages/InvestorMarketplacePage";
import ActivityFeedPage from "./pages/ActivityFeedPage";
import WatchlistPage from "./pages/WatchlistPage";
import AcquisitionsPage from "./pages/AcquisitionsPage";
import SellPage from "./pages/SellPage";
import InvestorsPage from "./pages/InvestorsPage";
import UnderwritingQueue from "./pages/UnderwritingQueue";
import UnderwritingDetail from "./pages/UnderwritingDetail";
import CapitalMatchingQueue from "./pages/CapitalMatchingQueue";
import CapitalMatchingDetail from "./pages/CapitalMatchingDetail";
import FundingQueue from "./pages/FundingQueue";
import FundingDetail from "./pages/FundingDetail";
import JumpBar, { trackEntityView } from "./components/shared/JumpBar";
import GlobalSearch from "./components/shared/GlobalSearch";
import CopilotPanel from "./components/CopilotPanel";
import ProactiveAlertPanel from "./components/ProactiveAlertPanel";
import ReviewPage from "./pages/ReviewPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Nav Dropdown Component ─────────────────────────────────
function NavDropdown({ label, items, page, testId }) {
  const { navigateTo } = usePageNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = items.some(i => i.key === page);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <motion.button type="button" onClick={() => setOpen(!open)} whileHover={{ y: -2 }}
        className={`flex items-center gap-1 text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${isActive ? "text-gold" : "text-cream/80 hover:text-cream"}`}>
        {label}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 z-50 min-w-[180px] rounded-xl border border-gold/20 bg-[#022c22]/95 backdrop-blur-xl shadow-xl overflow-hidden">
            {items.map(item => (
              <button type="button" key={item.key} onClick={() => { navigateTo(item.key); setOpen(false); }}
                data-testid={`nav-${item.key}`}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${page === item.key ? "text-gold bg-gold/10" : "text-cream/80 hover:text-cream hover:bg-white/5"}`}>
                {item.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Notification Bell with Polling + Toast ─────────────────
const NOTIF_CATEGORIES = {
  deal_message: { label: "Messages", icon: MessageCircle, cls: "text-blue-400 bg-blue-500/20", category: "messages" },
  message: { label: "Messages", icon: MessageCircle, cls: "text-blue-400 bg-blue-500/20", category: "messages" },
  deal_submitted: { label: "New Deal", icon: FileText, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
  stage_change: { label: "Stage Update", icon: TrendingUp, cls: "text-purple-400 bg-purple-500/20", category: "deals" },
  deal_stage_update: { label: "Stage Update", icon: TrendingUp, cls: "text-purple-400 bg-purple-500/20", category: "deals" },
  term_sheet: { label: "Term Sheet", icon: FileText, cls: "text-amber-400 bg-amber-500/20", category: "deals" },
  term_sheet_submitted: { label: "Term Sheet", icon: FileText, cls: "text-amber-400 bg-amber-500/20", category: "deals" },
  lender_interest: { label: "Lender Activity", icon: Users, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
  lender_match: { label: "Deal Match", icon: Handshake, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
  deal_match: { label: "New Match", icon: Handshake, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
  deal_funded: { label: "Funded!", icon: Banknote, cls: "text-yellow-400 bg-yellow-500/20", category: "deals" },
  new_opportunity: { label: "Opportunity", icon: TrendingUp, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
  funding_milestone: { label: "Milestone", icon: TrendingUp, cls: "text-yellow-400 bg-yellow-500/20", category: "deals" },
  investor_commitment: { label: "Commitment", icon: DollarSign, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
  closing_blocker: { label: "Blocker", icon: AlertTriangle, cls: "text-red-400 bg-red-500/20", category: "system" },
  health_alert: { label: "Alert", icon: AlertTriangle, cls: "text-red-400 bg-red-500/20", category: "system" },
  accreditation_review: { label: "Accreditation", icon: Shield, cls: "text-amber-400 bg-amber-500/20", category: "system" },
  accreditation_request: { label: "Accreditation", icon: Shield, cls: "text-amber-400 bg-amber-500/20", category: "system" },
  payment: { label: "Payment", icon: DollarSign, cls: "text-emerald-400 bg-emerald-500/20", category: "deals" },
};

// Celebration confetti for milestone notifications
function triggerCelebration() {
  const colors = ["#E5C98B", "#065f46", "#fbbf24", "#fff"];
  const particles = 40;
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999;overflow:hidden;";
  document.body.appendChild(container);
  for (let i = 0; i < particles; i++) {
    const p = document.createElement("div");
    const size = Math.random() * 8 + 4;
    const x = Math.random() * 100;
    const delay = Math.random() * 0.5;
    const duration = Math.random() * 1.5 + 1.5;
    p.style.cssText = `position:absolute;top:-10px;left:${x}%;width:${size}px;height:${size}px;background:${colors[i % colors.length]};border-radius:${Math.random() > 0.5 ? "50%" : "2px"};animation:confettiFall ${duration}s ${delay}s ease-in forwards;opacity:0;`;
    container.appendChild(p);
  }
  if (!document.getElementById("confetti-keyframes")) {
    const style = document.createElement("style");
    style.id = "confetti-keyframes";
    style.textContent = `@keyframes confettiFall{0%{opacity:1;transform:translateY(0) rotate(0deg) scale(1)}50%{opacity:1}100%{opacity:0;transform:translateY(100vh) rotate(${360 + Math.random() * 360}deg) scale(0.5)}}`;
    document.head.appendChild(style);
  }
  setTimeout(() => container.remove(), 4000);
}

function NotificationBell() {
  const { user } = useAuth();
  const { navigateTo, navigate } = usePageNavigate();
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("timeline"); // "timeline" | "grouped"
  const ref = useRef(null);
  const lastToastTime = useRef(new Date().toISOString());
  const shownToastIds = useRef(new Set());

  // Get auth header from localStorage (InternalLoginPage sets it there)
  const getAuthHeaders = useCallback(() => {
    const t = localStorage.getItem("ic_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchUnread = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, { headers });
      setUnread(data.unread || 0);
    } catch {}
  }, [getAuthHeaders]);

  const fetchNotifications = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, { headers });
      setNotifications((data.notifications || []).slice(0, 15));
      setUnread(data.unread || 0);
    } catch {}
  }, [getAuthHeaders]);

  // Toast polling — check for new high-priority notifications
  const pollToasts = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;
    try {
      const { data } = await axios.get(`${API}/notifications/toast`, {
        params: { since: lastToastTime.current },
        headers,
      });
      if (data.server_time) lastToastTime.current = data.server_time;

      (data.toasts || []).forEach(n => {
        const nid = n.notification_id || n.notif_id;
        if (shownToastIds.current.has(nid)) return;
        shownToastIds.current.add(nid);
        // Keep set manageable
        if (shownToastIds.current.size > 100) {
          const arr = [...shownToastIds.current];
          shownToastIds.current = new Set(arr.slice(-50));
        }

        const config = NOTIF_CATEGORIES[n.type] || { label: "Update", icon: Bell };
        const isMilestone = n.type === "deal_funded" || n.type === "funding_milestone";
        const priority = n.priority || "normal";

        if (isMilestone) {
          triggerCelebration();
          toast.success(n.title || config.label, {
            description: n.message,
            duration: 8000,
            action: n.link_to ? { label: "View", onClick: () => { if (n.link_to.startsWith("/")) { navigate(n.link_to); } else { navigateTo(n.link_to); } } } : undefined,
          });
        } else if (priority === "urgent") {
          toast(n.title || config.label, {
            description: n.message,
            duration: 6000,
            action: n.link_to ? { label: "View", onClick: () => { if (n.link_to.startsWith("/")) { navigate(n.link_to); } else { navigateTo(n.link_to); } } } : undefined,
          });
        } else {
          toast(n.title || config.label, {
            description: n.message,
            duration: 4000,
          });
        }
      });
    } catch {}
  }, [getAuthHeaders, navigateTo]);

  useEffect(() => {
    fetchUnread();
    const unreadInterval = setInterval(fetchUnread, 15000);
    const toastInterval = setInterval(pollToasts, 10000);
    // Initial toast poll after 3s to avoid splash
    const initialToast = setTimeout(pollToasts, 3000);
    return () => {
      clearInterval(unreadInterval);
      clearInterval(toastInterval);
      clearTimeout(initialToast);
    };
  }, [fetchUnread, pollToasts]);

  const handleOpen = () => {
    setOpen(!open);
    if (!open) fetchNotifications();
  };

  const markRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => (n.notification_id || n.notif_id) === notifId ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  // Compute filtered + grouped BEFORE early return (hooks must not be conditional)
  const filtered = filter === "all" ? notifications : notifications.filter(n => {
    const cat = NOTIF_CATEGORIES[n.type]?.category || "system";
    return cat === filter;
  });

  const categoryCounts = { messages: 0, deals: 0, system: 0 };
  notifications.filter(n => !n.read).forEach(n => {
    const cat = NOTIF_CATEGORIES[n.type]?.category || "system";
    categoryCounts[cat]++;
  });

  const groupedByDeal = useMemo(() => {
    const groups = {};
    const ungrouped = [];
    filtered.forEach(n => {
      const dealKey = n.lead_id || n.deal_id;
      if (dealKey) {
        if (!groups[dealKey]) {
          groups[dealKey] = {
            dealKey,
            dealNumber: n.deal_number || n.metadata?.deal_number || null,
            address: n.metadata?.property_address || null,
            items: [],
            latestAt: n.created_at,
            unreadCount: 0,
          };
        }
        groups[dealKey].items.push(n);
        if (!n.read) groups[dealKey].unreadCount++;
        if (n.created_at > groups[dealKey].latestAt) groups[dealKey].latestAt = n.created_at;
      } else {
        ungrouped.push(n);
      }
    });
    const sorted = Object.values(groups).sort((a, b) => (b.latestAt || "").localeCompare(a.latestAt || ""));
    return { groups: sorted, ungrouped };
  }, [filtered]);

  if (!user) return null;

  // ── Notification Click Routing Map ──
  const getNotifRoute = (n) => {
    const ntype = n.type || "";
    const leadId = n.lead_id || n.deal_id || n.entity_id;
    const role = user?.role;

    // Borrower routes
    if (role === "borrower") {
      if (["document_approved", "document_rejected", "doc_approved", "doc_rejected"].includes(ntype))
        return leadId ? `/dashboard?deal=${leadId}&tab=documents` : null;
      if (["term_sheet", "term_sheet_submitted", "term_sheet_received"].includes(ntype))
        return leadId ? `/dashboard?deal=${leadId}&tab=offers` : null;
      if (["stage_change", "deal_stage_update", "stage_transition"].includes(ntype))
        return leadId ? `/dashboard?deal=${leadId}&tab=overview` : null;
      if (["deal_message", "message", "info_request"].includes(ntype))
        return leadId ? `/dashboard?deal=${leadId}&tab=messages` : null;
      if (leadId) return `/dashboard?deal=${leadId}&tab=overview`;
    }

    // Lender routes
    if (role === "lender") {
      if (["deal_match", "lender_match", "new_deal_matched"].includes(ntype))
        return "/lender?section=marketplace";
      if (["term_sheet", "term_sheet_submitted", "term_sheet_selected"].includes(ntype))
        return "/lender?section=pipeline";
      if (["stage_change", "deal_stage_update"].includes(ntype))
        return "/lender?section=deal_flow";
      if (["lender_interest"].includes(ntype))
        return "/lender?section=marketplace";
      if (leadId) return `/lender?section=deal_flow`;
    }

    // Investor routes
    if (role === "investor") {
      if (["new_opportunity"].includes(ntype))
        return "/investor?section=opportunities";
      if (["distribution_credited", "payment"].includes(ntype))
        return "/investor?section=fund_portal";
      if (["accreditation_review", "accreditation_request", "accreditation_verified"].includes(ntype))
        return "/investor?section=fund_portal";
      if (["investor_commitment"].includes(ntype))
        return "/investor?section=portfolio";
      return "/investor?section=opportunities";
    }

    // Admin routes
    if (role === "admin" || role === "originations" || role === "underwriter") {
      if (["deal_submitted", "new_user_registered"].includes(ntype))
        return "/admin?section=borrowers";
      if (["underwriting_review_needed", "health_alert"].includes(ntype))
        return leadId ? `/entity/deal/${leadId}` : "/admin?section=underwriting";
      if (["founding_member_application"].includes(ntype))
        return "/admin?section=action-required";
      if (["investor_commitment"].includes(ntype))
        return "/admin?section=investors";
      if (["closing_blocker"].includes(ntype))
        return leadId ? `/entity/deal/${leadId}` : "/admin?section=deal_center";
      if (leadId) return `/entity/deal/${leadId}`;
      return "/admin?section=overview";
    }

    // Fallback: entity 360 page or null
    if (leadId) return `/entity/deal/${leadId}`;
    if (n.entity_type && n.entity_id) return `/entity/${n.entity_type}/${n.entity_id}`;
    return null;
  };

  const handleNotifClick = (n) => {
    const nid = n.notification_id || n.notif_id;
    markRead(nid);
    const route = n.link_to || getNotifRoute(n);
    if (route) {
      if (route.startsWith("/")) {
        navigate(route);
      } else {
        navigateTo(route);
      }
    }
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      <button type="button" onClick={handleOpen} className="relative p-2 rounded-lg text-cream/70 hover:text-cream hover:bg-white/10 transition-colors" data-testid="notification-bell-btn">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse" data-testid="notification-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[480px] rounded-2xl border border-[#EDE8DC] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.15)] overflow-hidden" data-testid="notification-dropdown">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-emerald-950 to-emerald-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-[#E5C98B]" />
                <span className="text-sm font-bold text-white">Notifications</span>
                {unread > 0 && <span className="text-[10px] font-bold text-[#E5C98B] bg-white/10 px-2 py-0.5 rounded-full">{unread} new</span>}
              </div>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[10px] font-medium text-white/60 hover:text-white transition-colors" data-testid="mark-all-read-btn">Mark all read</button>
              )}
            </div>

            {/* Category Tabs */}
            <div className="flex items-center justify-between px-3 py-2 bg-[#FDFCF9] border-b border-[#EDE8DC]">
              <div className="flex items-center gap-1">
                {[
                  { key: "all", label: "All", count: unread },
                  { key: "messages", label: "Messages", count: categoryCounts.messages },
                  { key: "deals", label: "Deals", count: categoryCounts.deals },
                  { key: "system", label: "System", count: categoryCounts.system },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setFilter(tab.key)}
                    data-testid={`notif-filter-${tab.key}`}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${filter === tab.key ? "bg-emerald-700 text-white" : "text-[#888] hover:text-[#555] hover:bg-[#f0ece4]"}`}>
                    {tab.label}
                    {tab.count > 0 && <span className={`px-1 rounded text-[8px] font-bold ${filter === tab.key ? "bg-white/20" : "bg-red-100 text-red-600"}`}>{tab.count}</span>}
                  </button>
                ))}
              </div>
              {/* View mode toggle */}
              <div className="flex items-center bg-[#f0ece4] rounded-lg p-0.5" data-testid="notif-view-mode">
                <button onClick={() => setViewMode("timeline")}
                  data-testid="notif-view-timeline"
                  className={`px-2 py-1 rounded-md text-[9px] font-semibold transition-all ${viewMode === "timeline" ? "bg-white text-[#333] shadow-sm" : "text-[#999]"}`}>
                  <Clock className="h-3 w-3" />
                </button>
                <button onClick={() => setViewMode("grouped")}
                  data-testid="notif-view-grouped"
                  className={`px-2 py-1 rounded-md text-[9px] font-semibold transition-all ${viewMode === "grouped" ? "bg-white text-[#333] shadow-sm" : "text-[#999]"}`}>
                  <FileText className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[340px]">
              {filtered.length === 0 ? (
                <div className="text-center py-10" data-testid="notification-empty-state">
                  <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-[#555]">You're all caught up</p>
                  <p className="text-xs text-[#aaa] mt-1">No new {filter === "all" ? "" : filter + " "}notifications</p>
                </div>
              ) : viewMode === "grouped" ? (
                /* ── GROUPED BY DEAL VIEW ── */
                <div>
                  {groupedByDeal.groups.map(group => (
                    <div key={group.dealKey} className="border-b border-[#f5f0e8]" data-testid={`notif-group-${group.dealKey}`}>
                      <div className="px-4 py-2 bg-[#FDFCF9] flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-6 h-6 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
                            <Building2 className="h-3 w-3 text-emerald-700" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-[#333] truncate block">
                              {group.dealNumber || group.address || `Deal ${group.dealKey.slice(0, 8)}`}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {group.unreadCount > 0 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-red-100 text-red-600">{group.unreadCount}</span>
                          )}
                          <span className="text-[9px] text-[#bbb]">{group.items.length}</span>
                        </div>
                      </div>
                      {group.items.slice(0, 3).map(n => {
                        const nid = n.notification_id || n.notif_id;
                        const config = NOTIF_CATEGORIES[n.type] || { label: "Update", icon: Bell, cls: "text-gray-400 bg-gray-100", category: "system" };
                        const Icon = config.icon;
                        const timeStr = n.created_at ? (() => { const diff = (Date.now() - new Date(n.created_at).getTime()) / 1000; if (diff < 60) return "Now"; if (diff < 3600) return `${Math.floor(diff / 60)}m`; if (diff < 86400) return `${Math.floor(diff / 3600)}h`; return `${Math.floor(diff / 86400)}d`; })() : "";
                        return (
                          <button key={nid} onClick={() => handleNotifClick(n)}
                            className={`w-full text-left px-4 pl-8 py-2 border-b border-[#faf7f2] transition-all cursor-pointer hover:bg-[#FDFCF9] ${!n.read ? "bg-emerald-50/30" : ""}`}
                            data-testid={`notification-item-${nid}`}>
                            <div className="flex items-start gap-2.5">
                              <div className={`h-6 w-6 rounded-lg flex items-center justify-center shrink-0 ${config.cls}`}>
                                <Icon className="h-3 w-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-[11px] leading-snug truncate ${!n.read ? "font-semibold text-[#0A0A0A]" : "text-[#555]"}`}>{n.title}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[9px] text-[#bbb]">{config.label}</span>
                                  <span className="text-[9px] text-[#bbb]">{timeStr}</span>
                                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {group.items.length > 3 && (
                        <button onClick={() => { if (group.dealKey) { navigate(`/entity/deal/${group.dealKey}`); setOpen(false); } }}
                          className="w-full text-center py-1.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50/50 transition-colors">
                          +{group.items.length - 3} more
                        </button>
                      )}
                    </div>
                  ))}
                  {groupedByDeal.ungrouped.map(n => {
                    const nid = n.notification_id || n.notif_id;
                    const config = NOTIF_CATEGORIES[n.type] || { label: "Update", icon: Bell, cls: "text-gray-400 bg-gray-100", category: "system" };
                    const Icon = config.icon;
                    const timeStr = n.created_at ? (() => { const diff = (Date.now() - new Date(n.created_at).getTime()) / 1000; if (diff < 60) return "Now"; if (diff < 3600) return `${Math.floor(diff / 60)}m`; if (diff < 86400) return `${Math.floor(diff / 3600)}h`; return `${Math.floor(diff / 86400)}d`; })() : "";
                    return (
                      <button key={nid} onClick={() => handleNotifClick(n)}
                        className={`w-full text-left px-4 py-3 border-b border-[#f5f0e8] transition-all cursor-pointer hover:bg-[#FDFCF9] ${!n.read ? "bg-emerald-50/40" : ""}`}
                        data-testid={`notification-item-${nid}`}>
                        <div className="flex items-start gap-3">
                          <div className={`h-8 w-8 rounded-xl flex items-center justify-center shrink-0 ${config.cls}`}><Icon className="h-4 w-4" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className={`text-xs leading-snug truncate ${!n.read ? "font-semibold text-[#0A0A0A]" : "text-[#555]"}`}>{n.title}</p>
                              <span className="text-[10px] text-[#bbb] shrink-0">{timeStr}</span>
                            </div>
                            <p className="text-[11px] text-[#888] mt-0.5 line-clamp-1">{n.message}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                filtered.map(n => {
                  const nid = n.notification_id || n.notif_id;
                  const config = NOTIF_CATEGORIES[n.type] || { label: "Update", icon: Bell, cls: "text-gray-400 bg-gray-100", category: "system" };
                  const Icon = config.icon;
                  const isMilestone = n.type === "deal_funded" || n.type === "funding_milestone";
                  const timeStr = n.created_at ? (() => {
                    const diff = (Date.now() - new Date(n.created_at).getTime()) / 1000;
                    if (diff < 60) return "Now";
                    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
                    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
                    return `${Math.floor(diff / 86400)}d`;
                  })() : "";

                  return (
                    <button key={nid} onClick={() => handleNotifClick(n)}
                      className={`w-full text-left px-4 py-3 border-b border-[#f5f0e8] transition-all cursor-pointer hover:bg-[#FDFCF9] group ${!n.read ? "bg-emerald-50/40" : ""} ${isMilestone && !n.read ? "bg-gradient-to-r from-amber-50/60 to-emerald-50/40 border-l-2 border-l-[#E5C98B]" : ""}`}
                      data-testid={`notification-item-${nid}`}>
                      <div className="flex items-start gap-3">
                        <div className={`h-8 w-8 rounded-xl flex items-center justify-center shrink-0 ${isMilestone ? "text-yellow-500 bg-yellow-500/20 ring-1 ring-yellow-300/50" : config.cls}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-xs leading-snug truncate ${!n.read ? "font-semibold text-[#0A0A0A]" : "text-[#555]"} ${isMilestone && !n.read ? "text-emerald-900" : ""}`}>{n.title}</p>
                            <span className="text-[10px] text-[#bbb] shrink-0">{timeStr}</span>
                          </div>
                          <p className="text-[11px] text-[#888] mt-0.5 line-clamp-1">{n.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[9px] font-medium uppercase ${isMilestone ? "text-[#E5C98B]" : "text-[#bbb]"}`}>{config.label}</span>
                            {!n.read && <span className={`w-1.5 h-1.5 rounded-full ${isMilestone ? "bg-yellow-500" : "bg-emerald-500"}`} />}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <button onClick={() => { navigateTo("activity-feed"); setOpen(false); }}
              className="w-full text-center py-3 text-xs font-bold text-emerald-700 hover:bg-[#FDFCF9] border-t border-[#EDE8DC] transition-colors flex items-center justify-center gap-1" data-testid="view-all-notifications-btn">
              View All Activity <ArrowRight className="h-3 w-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── User Dropdown ──────────────────────────────────────────
function UserDropdown({ user, page, logout }) {
  const { navigateTo } = usePageNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const INTERNAL_ROLE_SET = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);
  const isInternalUser = INTERNAL_ROLE_SET.has(user.role);
  const dashPage = isInternalUser ? "admin" : user.role === "investor" ? "investor" : user.role === "lender" ? "lender-portal" : "dashboard";

  const menuItems = [
    { key: dashPage, label: "Dashboard", icon: LayoutDashboard },
    { key: "profile", label: "My Profile", icon: User },
    { key: "activity-feed", label: "Activity Feed", icon: Bell },
    // Borrower-specific
    ...(user.role === "borrower" ? [{ key: "discovery", label: "Lender Directory", icon: Search }] : []),
    // Lender-specific — deals only, never opportunities
    ...(user.role === "lender" ? [{ key: "market", label: "Loan Market", icon: Store }] : []),
    // Investor-specific — opportunities only, never deals
    ...(user.role === "investor" ? [{ key: "investor-marketplace", label: "Opportunities", icon: Briefcase }] : []),
    // Admin/Internal — full system
    ...(isInternalUser ? [
      { key: "market", label: "Loan Market", icon: Store },
      { key: "investor-marketplace", label: "Opportunities", icon: Briefcase },
      { key: "discovery", label: "Lender Directory", icon: Search },
    ] : []),
    ...(user.role === "admin" || user.role === "underwriter" ? [{ key: "underwriting", label: "Underwriting", icon: Shield }] : []),
    ...(isInternalUser ? [{ key: "capital-matching", label: "Capital Matching", icon: Handshake }] : []),
    ...(user.role === "admin" || user.role === "underwriter" || user.role === "originations" ? [{ key: "funding", label: "Funding", icon: Banknote }] : []),
  ];

  return (
    <div ref={ref} className="relative" data-testid="user-dropdown">
      <motion.button type="button" onClick={() => setOpen(!open)} whileHover={{ scale: 1.02 }}
        className="flex items-center gap-2 px-3 py-2 rounded-full border border-gold/40 bg-gold/15 text-xs font-semibold text-gold hover:bg-gold/25 transition-colors">
        <div className="h-6 w-6 rounded-full bg-gold/30 flex items-center justify-center text-[10px] font-bold text-[#022c22]">
          {user.full_name.charAt(0).toUpperCase()}
        </div>
        <span className="hidden sm:inline max-w-[80px] truncate">{user.full_name.split(" ")[0]}</span>
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 z-50 w-52 rounded-xl border border-gold/20 bg-[#022c22]/95 backdrop-blur-xl shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gold/15">
              <p className="text-sm font-semibold text-cream truncate">{user.full_name}</p>
              <p className="text-xs text-cream/50 truncate">{user.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-gold/15 text-[10px] font-bold text-gold uppercase tracking-wider">{user.role}</span>
            </div>
            {menuItems.map(item => (
              <button type="button" key={item.key} onClick={() => { navigateTo(item.key); setOpen(false); }}
                data-testid={`user-menu-${item.key}`}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${page === item.key ? "text-gold bg-gold/10" : "text-cream/80 hover:text-cream hover:bg-white/5"}`}>
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
            <div className="border-t border-gold/15">
              <button type="button" onClick={() => { logout(); navigateTo("home"); setOpen(false); }}
                data-testid="user-menu-logout"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400/80 hover:text-red-400 hover:bg-red-500/5 transition-colors">
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Page Navigation ────────────────────────────────────────
function PageNav({ page }) {
  const { navigateTo, navigateToEntity } = usePageNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on page change
  useEffect(() => { setMobileOpen(false); }, [page]);

  const navItems = (() => {
    const publicItems = [
      { key: "home", label: "Home" },
      { key: "about", label: "About" },
      { key: "acquisitions", label: "Acquisitions" },
      { key: "sell", label: "Sell to Us" },
      { key: "investors", label: "Investors" },
      { key: "insights", label: "Insights" },
      { key: "contact", label: "Contact" },
    ];
    if (!user) return publicItems;
    const role = user.role;
    const dashKey = role === "admin" ? "admin" : role === "investor" ? "investor" : role === "lender" ? "lender-portal" : "dashboard";
    return [
      { key: "home", label: "Home" },
      { key: dashKey, label: "Dashboard" },
      ...(role === "lender" ? [{ key: "market", label: "Loan Market" }] : []),
      ...(role === "investor" ? [{ key: "investor-marketplace", label: "Opportunities" }] : []),
      ...(INTERNAL_ROLES.has(role) ? [
        { key: "market", label: "Loan Market" },
        { key: "investor-marketplace", label: "Opportunities" },
      ] : []),
      { key: "insights", label: "Insights" },
      { key: "contact", label: "Contact" },
    ];
  })();

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#022c22]/95 backdrop-blur-2xl border-b border-gold/20 shadow-[0_4px_30px_rgba(2,44,34,0.5)]" : "bg-[#022c22]/80 backdrop-blur-xl border-b border-gold/15"}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8">
        <motion.button type="button" onClick={() => navigateTo("home")} className="text-left shrink-0" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} data-testid="header-logo-btn">
          <Logo size="small" showTagline={false} />
        </motion.button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="main-navigation">
          {navItems.map(item => (
            <motion.button type="button" key={item.key} onClick={() => navigateTo(item.key)} whileHover={{ y: -2 }} data-testid={`nav-${item.key}`}
              className={`relative text-sm font-medium transition-all duration-300 whitespace-nowrap ${page === item.key ? "text-gold" : "text-cream/80 hover:text-cream"}`}>
              {item.label}
              {item.badge && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded-full bg-[#E5C98B] text-[#022c22] align-top">{item.badge}</span>}
              {page === item.key && <motion.div layoutId="navIndicator" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent" />}
            </motion.button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {user ? (
            <>
              <div className="hidden sm:block"><GlobalSearch onNavigate={(type, id) => navigateToEntity(type, id)} /></div>
              <NotificationBell />
              <UserDropdown user={user} page={page} logout={logout} />
            </>
          ) : (
            <motion.button type="button" onClick={() => navigateTo("auth")} whileHover={{ scale: 1.05 }} data-testid="nav-login"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 bg-gold/15 text-sm font-semibold text-gold hover:bg-gold/25 transition-colors">
              <LogIn className="h-4 w-4 text-gold" /> Sign In
            </motion.button>
          )}
          {user ? (
            user.role === "borrower" ? (
              <GoldButton onClick={() => navigateTo("apply")} className="shrink-0 !px-4 !py-2 sm:!px-6 sm:!py-2.5 !text-xs sm:!text-sm" data-testid="header-apply-btn">
                <span className="hidden sm:inline">New Deal</span>
                <span className="sm:hidden">+</span>
              </GoldButton>
            ) : null
          ) : (
            <GoldButton onClick={() => navigateTo("sell")} className="shrink-0 !px-4 !py-2 sm:!px-6 sm:!py-2.5 !text-xs sm:!text-sm" data-testid="header-apply-btn">
              <span className="hidden sm:inline">Submit a Property</span>
              <span className="sm:hidden">Submit</span>
            </GoldButton>
          )}

          {/* Mobile Hamburger */}
          <button type="button" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 rounded-lg text-cream/80 hover:text-cream hover:bg-white/10 transition-colors" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden border-t border-gold/15 bg-[#022c22]/98 backdrop-blur-2xl"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4 gap-1">
              {navItems.map(item => (
                <button type="button" key={item.key} onClick={() => navigateTo(item.key)} data-testid={`mobile-nav-${item.key}`}
                  className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${page === item.key ? "text-gold bg-gold/10" : "text-cream/80 hover:text-cream hover:bg-white/5"}`}>
                  {item.label}
                  {item.badge && <span className="ml-1.5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded-full bg-[#E5C98B] text-[#022c22]">{item.badge}</span>}
                </button>
              ))}
              {!user && (
                <button type="button" onClick={() => navigateTo("auth")} data-testid="mobile-nav-signin"
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-gold hover:bg-gold/10 transition-all mt-2 border-t border-gold/15 pt-4">
                  <LogIn className="h-4 w-4" /> Sign In
                </button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ── Footer ─────────────────────────────────────────────────
function Footer() {
  const { navigateTo } = usePageNavigate();
  const { user } = useAuth();
  return (
    <footer className="relative border-t border-gold/20 bg-gradient-to-b from-[#03352a] via-[#022c22] to-[#011a14]" data-testid="footer">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[200px] bg-emerald-600/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[150px] bg-gold/10 rounded-full blur-[80px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        {/* Main Footer Grid */}
        <div className="grid gap-8 sm:gap-10 grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <motion.button type="button" onClick={() => navigateTo("home")} className="text-left" whileHover={{ scale: 1.02 }} data-testid="footer-logo-btn">
              <Logo size="default" showTagline={true} />
            </motion.button>
            <p className="mt-4 text-sm leading-6 italic text-cream/70">Commercial Real Estate the Way It Should Be Done.</p>
            <p className="mt-2 text-xs leading-5 text-cream/50">Imaani Capital acquires multifamily, retail, and industrial properties across the United States.</p>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-gold/80 mb-4">Company</h4>
            <nav className="flex flex-col gap-2.5">
              {[
                { key: "about", label: "About" },
                { key: "acquisitions", label: "Acquisitions" },
                { key: "contact", label: "Contact" },
                { key: "insights", label: "Insights" },
              ].map(item => (
                <motion.button type="button" key={item.key} onClick={() => navigateTo(item.key)} className="text-sm text-cream/65 hover:text-gold transition-colors text-left"
                  whileHover={{ x: 3 }} data-testid={`footer-nav-${item.key}`}>{item.label}</motion.button>
              ))}
            </nav>
          </div>

          {/* Acquisitions */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-gold/80 mb-4">Acquisitions</h4>
            <nav className="flex flex-col gap-2.5">
              {[
                { key: "acquisitions", label: "What We Buy" },
                { key: "sell", label: "Submit a Property" },
                { key: "investors", label: "Capital Partners" },
              ].map(item => (
                <motion.button type="button" key={item.key} onClick={() => navigateTo(item.key)} className="text-sm text-cream/65 hover:text-gold transition-colors text-left"
                  whileHover={{ x: 3 }} data-testid={`footer-nav-${item.key}`}>{item.label}</motion.button>
              ))}
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-gold/80 mb-4">Legal</h4>
            <nav className="flex flex-col gap-2.5">
              {[
                { key: "terms", label: "Terms of Service" },
                { key: "privacy", label: "Privacy Policy" },
                { key: "risk-disclosure", label: "Risk Disclosure" },
                { key: "accredited-notice", label: "Accredited Investor Notice" },
                { key: "platform-role", label: "Platform Role Disclosure" },
              ].map(item => (
                <motion.button type="button" key={item.key} onClick={() => navigateTo(item.key)} className="text-sm text-cream/65 hover:text-gold transition-colors text-left"
                  whileHover={{ x: 3 }} data-testid={`footer-nav-${item.key}`}>{item.label}</motion.button>
              ))}
            </nav>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-gold/10">
          <p className="text-[11px] text-cream/40 text-center mb-3">Texas Commercial Real Estate Investment — National Reach.</p>
        </div>

        <div className="text-center">
          <p className="text-[10px] leading-5 text-cream/35 max-w-4xl mx-auto mb-4">
            Imaani Capital LLC is a commercial real estate investment firm headquartered in Houston, Texas. Investment opportunities are available to accredited investors only as defined by SEC Rule 501. Nothing on this website constitutes an offer to sell securities or a guarantee of returns. All real estate investments involve risk.
          </p>
          <div className="text-xs text-cream/50">&copy; 2026 Imaani Capital. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}

// ── Entity Route Wrapper ───────────────────────────────────
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

function EntityRouteWrapper({ onBack }) {
  const { entityType, entityId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 2) navigate(-1);
    else onBack();
  };

  // Role-based entity access gating — HARD SEPARATION
  if (user) {
    const role = user.role || "";
    const isInternal = INTERNAL_ROLES.has(role);
    if (!isInternal) {
      // LENDERS: can see deals (their deals in marketplace), never opportunities/commitments/distributions
      if (role === "lender") {
        if (["opportunity", "commitment", "distribution", "fund", "investor"].includes(entityType)) {
          return <div className="p-12 text-center text-[#888]" data-testid="access-denied">Access denied — Lender accounts cannot view investor content.</div>;
        }
      }
      // INVESTORS: can see opportunities/commitments, never raw deals
      if (role === "investor") {
        if (["deal", "lender", "borrower"].includes(entityType)) {
          return <div className="p-12 text-center text-[#888]" data-testid="access-denied">Access denied — Investor accounts cannot view deal content directly.</div>;
        }
      }
      // BORROWERS: can see their deals, never opportunities/commitments/investor content/lender content
      if (role === "borrower") {
        if (["opportunity", "commitment", "distribution", "fund", "investor", "lender"].includes(entityType)) {
          return <div className="p-12 text-center text-[#888]" data-testid="access-denied">Access denied</div>;
        }
      }
      // No external user can see internal-only entities
      if (["report", "reports", "exception"].includes(entityType)) {
        return <div className="p-12 text-center text-[#888]" data-testid="access-denied">Access denied</div>;
      }
    }
  }

  const e360props = { onBack: handleBack, breadcrumbs: [] };

  switch (entityType) {
    case "deal": return <Deal360Page leadId={entityId} {...e360props} />;
    case "borrower": return <Borrower360Page userId={entityId} {...e360props} />;
    case "lender": return <Lender360Page userId={entityId} {...e360props} />;
    case "investor": return <Investor360Page userId={entityId} {...e360props} />;
    case "opportunity": return <Opportunity360Page oppId={entityId} {...e360props} />;
    case "commitment": return <Commitment360Page commitmentId={entityId} {...e360props} />;
    case "document": return <Document360Page docId={entityId} {...e360props} />;
    case "listing": return <Listing360Page entityId={entityId} {...e360props} />;
    case "fund": return <Fund360Page fundId={entityId} {...e360props} />;
    case "exception": return <Exception360Page exceptionId={entityId} {...e360props} />;
    case "distribution": return <Distribution360Page distributionId={entityId} {...e360props} />;
    case "report": return <Report360Page reportId={entityId} {...e360props} />;
    case "reports": return <ReportCenterPage {...e360props} />;
    default: return <div className="p-12 text-center text-[#888]">Entity type not found</div>;
  }
}

// ── Main App ───────────────────────────────────────────────
function AppContent() {
  const [page, setPage] = useState("home");
  const [uwDealId, setUwDealId] = useState(null);
  const [cmDealId, setCmDealId] = useState(null);
  const [fundDealId, setFundDealId] = useState(null);
  const { user, loading, updateUser } = useAuth();
  const [urlAction, setUrlAction] = useState(null);
  const [urlToken, setUrlToken] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const closeEntityView = () => {
    if (user?.role === "admin") navigate("/admin");
    else if (user?.role === "investor") navigate("/investor");
    else if (user?.role === "lender") navigate("/lender");
    else navigate("/dashboard");
  };

  // Jump Bar (Cmd+J / Ctrl+J / Cmd+K / Ctrl+K)
  const [jumpBarOpen, setJumpBarOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "j" || e.key === "k")) { e.preventDefault(); setJumpBarOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Sync page state from URL for non-entity routes
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/entity/")) return;
    const routeMap = {
      "/": "home", "/about": "about", "/auth": "auth", "/login": "auth", "/dashboard": "dashboard",
      "/lender": "lender-portal", "/investor": "investor", "/admin": "admin",
      "/profile": "profile", "/discovery": "discovery", "/market": "market",
      "/loan-programs": "loan-programs", "/capital-partners": "capital-partners",
      "/how-it-works": "loan-programs", "/for-investors": "capital-partners",
      "/apply": "apply", "/contact": "contact", "/terms": "terms",
      "/privacy": "privacy", "/insights": "insights", "/for-lenders": "for-lenders",
      "/founding-members": "founding-members",
      "/sell": "sell", "/acquisitions": "acquisitions", "/investors": "investors",
      "/secondary-market": "secondary-market",
      "/risk-disclosure": "risk-disclosure", "/accredited-notice": "accredited-notice",
      "/platform-role": "platform-role", "/funding": "funding",
      "/logout": "logout",
    };
    const mapped = routeMap[path];
    if (mapped && mapped !== page) setPage(mapped);
  }, [location.pathname]);

  // Navigation helper that also pushes URL
  const setPageWithUrl = useCallback((newPage) => {
    setPage(newPage);
    if (newPage !== "underwriting") setUwDealId(null);
    if (newPage !== "capital-matching") setCmDealId(null);
    if (newPage !== "funding") setFundDealId(null);
    const pageRouteMap = {
      "home": "/", "about": "/about", "auth": "/auth", "dashboard": "/dashboard",
      "lender-portal": "/lender", "investor": "/investor", "admin": "/admin",
      "profile": "/profile", "discovery": "/discovery", "market": "/market",
      "loan-programs": "/loan-programs", "capital-partners": "/capital-partners",
      "apply": "/apply", "contact": "/contact", "terms": "/terms",
      "privacy": "/privacy", "insights": "/insights", "for-lenders": "/for-lenders",
      "founding-members": "/founding-members",
      "sell": "/sell", "acquisitions": "/acquisitions", "investors": "/investors",
      "secondary-market": "/secondary-market",
      "investor-marketplace": "/investor-marketplace",
      "activity-feed": "/activity-feed",
      "watchlist": "/watchlist",
      "risk-disclosure": "/risk-disclosure", "accredited-notice": "/accredited-notice",
      "platform-role": "/platform-role", "underwriting": "/underwriting", "capital-matching": "/capital-matching", "funding": "/funding",
    };
    const url = pageRouteMap[newPage];
    if (url) navigate(url);
  }, [navigate]);

  // Handle URL params on mount (verify-email, reset-password)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");
    const token = params.get("token");
    if (action && token) {
      setUrlAction(action);
      setUrlToken(token);
      navigate(location.pathname, { replace: true });

      if (action === "verify-email") {
        axios.get(`${API}/auth/verify-email?token=${token}`)
          .then(res => { setVerifyStatus({ success: true, message: res.data.message }); })
          .catch(err => {
            const detail = err.response?.data?.detail;
            setVerifyStatus({ success: false, message: typeof detail === "string" ? detail : "Verification failed" });
          });
      }
    }
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page, location.pathname]);

  // Handle /logout route
  useEffect(() => {
    if (page === "logout") {
      logout();
      setPage("home");
      navigate("/", { replace: true });
    }
  }, [page]);

  if (loading) return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  // Show email verification result
  if (verifyStatus) {
    return (
      <div className="min-h-screen bg-emerald-950 flex items-center justify-center" data-testid="verify-result-page">
        <div className="text-center px-6">
          <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-6 ${verifyStatus.success ? "bg-emerald-500/20 border border-emerald-400/40" : "bg-red-500/20 border border-red-400/40"}`}>
            {verifyStatus.success ? <span className="text-4xl text-emerald-400">&#10003;</span> : <span className="text-4xl text-red-400">&#10005;</span>}
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">{verifyStatus.success ? "Email Verified!" : "Verification Failed"}</h2>
          <p className="text-sm text-cream/60 mb-6">{verifyStatus.message}</p>
          <GoldButton onClick={() => { setVerifyStatus(null); setPageWithUrl("auth"); }} data-testid="verify-go-login">
            {verifyStatus.success ? "Sign In" : "Try Again"}
          </GoldButton>
        </div>
      </div>
    );
  }

  // Show reset password form
  if (urlAction === "reset-password" && urlToken) {
    return <ResetPasswordPage token={urlToken} onDone={() => { setUrlAction(null); setUrlToken(null); setPageWithUrl("auth"); }} />;
  }

  // Show accept-invite page
  if (urlAction === "accept-invite" && urlToken) {
    return <AcceptInvitePage token={urlToken} onAccepted={(u) => { setUrlAction(null); setUrlToken(null); updateUser(u); handleLogin(u); }} />;
  }

  // Show onboarding if logged in but not completed
  const needsOnboarding = user && user.role === "borrower" && user.onboarding_complete === false;
  if (needsOnboarding) {
    return <OnboardingPage onComplete={(updatedUser) => updateUser(updatedUser)} />;
  }

  // Show investor onboarding if logged in as investor but not completed
  const needsInvestorOnboarding = user && user.role === "investor" && user.investor_onboarding_complete === false;
  if (needsInvestorOnboarding) {
    return <InvestorOnboardingPage onComplete={(updatedUser) => updateUser(updatedUser)} />;
  }

  const handleLogin = (u) => {
    // Update AuthContext with the new user (critical for dedicated login pages)
    updateUser(u);
    const role = u.role;
    const dashRoute = u.computed_state?.dashboard_route;
    if (dashRoute) {
      setPageWithUrl(dashRoute);
    } else if (INTERNAL_ROLES.has(role)) {
      setPageWithUrl("admin");
    } else if (role === "investor") {
      setPageWithUrl("investor");
    } else if (role === "lender") {
      setPageWithUrl("lender-portal");
    } else {
      setPageWithUrl("dashboard");
    }
  };

  const pageContent = (() => {
    // Entity pages are handled by React Router <Routes> below
    if (location.pathname.startsWith("/entity/")) return null;

    switch (page) {
      case "auth": return <AuthPage onLogin={handleLogin} />;
      case "dashboard": return (user && (user.role === "borrower" || user.role === "admin")) ? <BorrowerDashboard /> : <AuthPage onLogin={handleLogin} />;
      case "lender-portal": {
        if (!user || (user.role !== "lender" && !INTERNAL_ROLES.has(user.role))) return <AuthPage onLogin={handleLogin} />;
        if (user.role === "lender" && user.status === "pending") return <PendingApproval />;
        if (user.role === "lender" && user.approval_status && user.approval_status !== "approved") {
          return <ConditionalAccessView role="lender" user={user} onLogout={() => { logout(); setPageWithUrl("home"); }} />;
        }
        return <LenderPortal />;
      }
      case "investor": {
        if (!user || (user.role !== "investor" && !INTERNAL_ROLES.has(user.role))) return <AuthPage onLogin={handleLogin} />;
        if (user.role === "investor" && user.approval_status && user.approval_status !== "approved") {
          return <ConditionalAccessView role="investor" user={user} onLogout={() => { logout(); setPageWithUrl("home"); }} />;
        }
        return <InvestorDashboard />;
      }
      case "admin": return (user && INTERNAL_ROLES.has(user.role)) ? <AdminDashboard /> : <AuthPage onLogin={handleLogin} />;
      case "profile": return user ? <ProfilePage /> : <AuthPage onLogin={handleLogin} />;
      case "discovery": return (user && (user.role === "borrower" || user.role === "admin")) ? <LenderDiscovery /> : <AuthPage onLogin={handleLogin} />;
      case "market": return (user && (user.role === "lender" || INTERNAL_ROLES.has(user.role))) ? <LoanMarket /> : <AuthPage onLogin={handleLogin} />;
      case "about": return <AboutPage />;
      case "sell": return <SellPage />;
      case "acquisitions": return <AcquisitionsPage />;
      case "investors": return <InvestorsPage />;
      case "loan-programs": return <LoanProgramsPage />;
      case "capital-partners": return <CapitalPartnersPage />;
      case "apply": return <ApplyPage />;
      case "contact": return <ContactPage />;
      case "terms": return <TermsPage />;
      case "privacy": return <PrivacyPage />;
      case "risk-disclosure": return <RiskDisclosurePage />;
      case "accredited-notice": return <AccreditedNoticePage />;
      case "platform-role": return <PlatformRolePage />;
      case "insights": return <InsightsPage />;
      case "founding-members": return <FoundingMembersPage />;
      case "for-lenders": return <LenderOnboardPage />;
      case "secondary-market": return (user && (user.role === "investor" || INTERNAL_ROLES.has(user.role))) ? <SecondaryMarketPage /> : <AuthPage onLogin={handleLogin} />;
      case "investor-marketplace": return (user && (user.role === "investor" || INTERNAL_ROLES.has(user.role))) ? <InvestorMarketplacePage /> : <AuthPage onLogin={handleLogin} />;
      case "activity-feed": return user ? <ActivityFeedPage /> : <AuthPage onLogin={handleLogin} />;
      case "underwriting": return (user?.role === "admin" || user?.role === "underwriter") ? (
          uwDealId
            ? <UnderwritingDetail leadId={uwDealId} onBack={() => setUwDealId(null)} />
            : <UnderwritingQueue onDealSelect={(id) => setUwDealId(id)} />
        ) : <AuthPage onLogin={handleLogin} />;
      case "capital-matching": return (user && (user.role === "admin" || user.role === "underwriter" || user.role === "originations" || user.role === "lender_relations")) ? (
          cmDealId
            ? <CapitalMatchingDetail leadId={cmDealId} onBack={() => setCmDealId(null)} />
            : <CapitalMatchingQueue onDealSelect={(id) => setCmDealId(id)} />
        ) : <AuthPage onLogin={handleLogin} />;
      case "funding": return (user?.role === "admin" || user?.role === "underwriter" || user?.role === "originations") ? (
          fundDealId
            ? <FundingDetail leadId={fundDealId} onBack={() => setFundDealId(null)} />
            : <FundingQueue onDealSelect={(id) => setFundDealId(id)} />
        ) : <AuthPage onLogin={handleLogin} />;
      case "watchlist": return user ? <WatchlistPage /> : <AuthPage onLogin={handleLogin} />;
      default: return <HomePage />;
    }
  })();

  // Dedicated login pages and mock pages render without nav/footer shell
  const pathname = location.pathname;
  if (pathname === "/internal/login" || pathname === "/investor/login" || pathname === "/lender/login") {
    return (
      <Routes>
        <Route path="/internal/login" element={<InternalLoginPage onLogin={handleLogin} />} />
        <Route path="/investor/login" element={<InvestorLoginPage onLogin={handleLogin} />} />
        <Route path="/lender/login" element={<LenderLoginPage onLogin={handleLogin} />} />
      </Routes>
    );
  }


  return (
    <div className="min-h-screen bg-cream text-[#0A0A0A] antialiased" data-testid="main-container">
      <PageNav page={page} />
      <Routes>
        <Route path="/review/:role" element={<ReviewPage />} />
        <Route path="/entity/:entityType/:entityId" element={
          <EntityRouteWrapper onBack={closeEntityView} />
        } />
        <Route path="*" element={
          <AnimatePresence mode="wait">
            <motion.main key={page} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              {pageContent}
            </motion.main>
          </AnimatePresence>
        } />
      </Routes>
      <Footer />
      {!user && <ChatWidget user={user} />}
      <JumpBar isOpen={jumpBarOpen} onClose={() => setJumpBarOpen(false)} />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      refetchOnWindowFocus: true,
      retry: 1,
      onError: (err) => console.error('Query error:', err),
    },
  },
});

export default function ImaaniCapitalMarketplace() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppContent />
            <Toaster
              position="top-right"
              richColors
              closeButton
              duration={4000}
              toastOptions={{
                style: { fontFamily: "inherit", borderRadius: "12px" },
                className: "shadow-lg",
              }}
            />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
